import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { cargoMediaSrc } from '@/lib/bubbleImage';
import CargoComposer from './CargoComposer';
import DriftCompose from './DriftCompose';
import { Package, Wind, NotePencil, Microphone, Camera, X, Sparkle, PaperPlaneTilt } from '@phosphor-icons/react';

const KIND_ICON = { wish: NotePencil, voice: Microphone, photo: Camera, clip: Microphone };
const KIND_COLOR = { wish: 'text-bx-cyan', voice: 'text-bx-magenta', photo: 'text-bx-volt', clip: 'text-bx-gold' };

/**
 * Panel on BubbleDetail. Shows the bubble's active cargo + lets creator:
 *  - attach a new moment (CargoComposer)
 *  - send the bubble drifting with that cargo (DriftCompose)
 *  - release a cargo before it drifts
 */
const CargoPanel = ({ bubble, isOwner, onUpdate }) => {
  const [active, setActive] = useState([]);
  const [slotsUsed, setSlotsUsed] = useState(0);
  const [slotsTotal, setSlotsTotal] = useState(2);
  const [loading, setLoading] = useState(true);
  const [composerOpen, setComposerOpen] = useState(false);
  const [driftOpen, setDriftOpen] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/bubbles/${bubble.id}/cargo`);
      setActive(data.active || []);
      setSlotsUsed((data.active || []).length);
      setSlotsTotal(bubble.cargo_slots || 2);
    } catch (err) { /* silent */ }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [bubble.id, bubble.cargo_slots]);

  const unlockSlot = async () => {
    if (!window.confirm('Spend 25 ✦ Resonance to give this Bubble +1 cargo slot?')) return;
    try {
      const { data } = await api.post(`/bubbles/${bubble.id}/unlock-cargo-slot`);
      toast.success(`+1 slot · now ${data.new_slot_count}/5`);
      setSlotsTotal(data.new_slot_count);
      onUpdate?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'could not');
    }
  };

  const release = async (cargoId) => {
    try {
      await api.delete(`/bubbles/${bubble.id}/cargo/${cargoId}`);
      toast('cargo released');
      await load(); onUpdate?.();
    } catch (err) { toast.error('could not release'); }
  };

  if (!isOwner && active.length === 0) return null;

  return (
    <div data-testid="cargo-panel" className="glass rounded-2xl p-5">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Package size={18} weight="duotone" className="text-bx-cyan" />
          <h3 className="font-display font-bold">Cargo</h3>
          {bubble.drifting && (
            <span className="ml-2 text-[10px] uppercase tracking-widest text-bx-magenta animate-pulse">
              · drifting
            </span>
          )}
        </div>
        {isOwner && !bubble.drifting && (
          <div className="flex items-center gap-2">
            <span className="text-[10px] uppercase tracking-widest text-white/60">
              {slotsUsed}/{slotsTotal}
            </span>
            {slotsTotal < 5 && (
              <button
                data-testid="cargo-unlock-slot"
                onClick={unlockSlot}
                title="Spend 25 Resonance to unlock +1 slot"
                className="text-[10px] px-2 py-1 rounded-full bg-bx-volt/20 hover:bg-bx-volt/30 text-bx-volt font-bold uppercase tracking-widest">
                +slot · 25 ✦
              </button>
            )}
            <button
              data-testid="cargo-add-btn"
              onClick={() => setComposerOpen(true)}
              className="text-xs px-3 py-1.5 rounded-full bg-white/10 hover:bg-white/15 font-bold uppercase tracking-widest"
            >
              + add moment
            </button>
          </div>
        )}
      </div>

      {loading ? (
        <div className="text-white/60 text-sm">listening…</div>
      ) : active.length === 0 ? (
        <div className="text-sm text-white/65">
          {isOwner ? (
            <>Your Bubble isn't carrying anything yet. Give it a wish, a voice note, a photo, or a clip — and then send it somewhere.</>
          ) : (
            <>This Bubble has nothing to share right now.</>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          {active.map((c) => {
            const Icon = KIND_ICON[c.kind] || Sparkle;
            const colorClass = KIND_COLOR[c.kind] || 'text-bx-cyan';
                            const audioSrc = c.kind === 'voice' ? cargoMediaSrc(c) : null;
            const imgSrc = c.kind === 'photo' ? cargoMediaSrc(c) : null;
            const clipSrc = c.kind === 'clip' ? cargoMediaSrc(c) : null;
            return (
              <motion.div key={c.id} initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }}
                className="rounded-2xl border border-white/10 bg-white/[0.03] p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Icon size={14} weight="duotone" className={colorClass} />
                  <span className="text-[10px] uppercase tracking-widest text-white/60">{c.kind}</span>
                  {c.moment && (
                    <span className="text-[10px] uppercase tracking-widest text-bx-volt">· {c.moment}</span>
                  )}
                  {isOwner && (
                    <button onClick={() => release(c.id)}
                      data-testid={`cargo-release-${c.id}`}
                      className="ml-auto p-1 rounded-full hover:bg-white/10 text-white/60">
                      <X size={12} weight="bold" />
                    </button>
                  )}
                </div>
                {c.kind === 'wish' && c.text && (
                  <div className="font-display text-base italic leading-snug">"{c.text}"</div>
                )}
                {audioSrc && (
                  <audio controls src={audioSrc} className="w-full mt-1" data-testid={`cargo-audio-${c.id}`} />
                )}
                {imgSrc && (
                  <img src={imgSrc} alt="" className="rounded-xl max-h-48 w-full object-cover mt-1" />
                )}
                {clipSrc && (
                  <video controls playsInline src={clipSrc}
                    data-testid={`cargo-clip-${c.id}`}
                    className="rounded-xl max-h-64 w-full mt-1" />
                )}
                {c.commentary && (
                  <div className="text-xs italic text-white/60 mt-2 border-l-2 border-bx-cyan/40 pl-3">
                    — {bubble.name}: {c.commentary}
                  </div>
                )}
              </motion.div>
            );
          })}

          {isOwner && active.length > 0 && !bubble.drifting && (
            <button
              data-testid="cargo-drift-btn"
              onClick={() => setDriftOpen(true)}
              className="w-full mt-2 btn-volt py-3 rounded-full font-bold text-sm flex items-center justify-center gap-2"
            >
              <PaperPlaneTilt size={16} weight="fill" />
              Send {bubble.name} drifting
            </button>
          )}
          {bubble.drifting && (
            <div className="text-[11px] text-white/65 flex items-center justify-center gap-1">
              <Wind size={12} weight="bold" /> in transit — will return with stories
            </div>
          )}
        </div>
      )}

      <CargoComposer
        bubble={bubble}
        open={composerOpen}
        onClose={() => setComposerOpen(false)}
        onAttached={() => { load(); onUpdate?.(); }}
      />
      <DriftCompose
        bubble={bubble}
        open={driftOpen}
        onClose={() => setDriftOpen(false)}
        onSent={() => { load(); onUpdate?.(); }}
      />
    </div>
  );
};

export default CargoPanel;
